import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Box } from '@repo/ui/Box';
import { Icon } from '@repo/ui/Icon';
import type { IconNames } from '@repo/ui/Icon/utils';
import { iconNameOptions } from '@repo/ui/Icon/utils';
import { rawString, Text } from '../Language/Language';
import { useSafeWorkflow } from '../../workflow/hooks';
import s from './ConfirmationEmail.module.css';
import { Flex } from '@repo/ui/Flex';
import { Button } from '@repo/ui/Button';
import { InputControl } from '@repo/ui/Input/InputControl';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputMessage } from '@repo/ui/Input/InputMessage';
import { InputRoot } from '@repo/ui/Input/InputRoot';

import { sendEmailMutationFn } from '../../graphql/mutation/sendEmail';
import { clientLogger } from '../../utils/logger';
import { useToast } from '@repo/ui/Toast/ToastRoot';
import { useSearch } from '@tanstack/react-router';
import type { OrderToken, Order } from '../../graphql/query/getOrder';

const EmailFormSchema = z.object({
  email: z
    .string()
    .min(1, {
      message: rawString({
        langKey: 'Global.confirmation.email.resend.requiredError',
      }),
    })
    .email({
      message: rawString({
        langKey: 'Global.confirmation.email.resend.validationError',
      }),
    }),
});

export type EmailFormSchemaType = z.infer<typeof EmailFormSchema>;

export type ConfirmationEmailProps = {
  email?: string;
  requestId?: string;
  iconKey?: string;
  descriptionKey?: string;
};

export const ConfirmationEmail = (props: ConfirmationEmailProps) => {
  const { requestId, email, iconKey, descriptionKey } = props;
  const [isInputOpen, setIsInputOpen] = useState<boolean>(false);
  const workflow = useSafeWorkflow();
  const params = useSearch({ strict: false });
  const { errorToast } = useToast();
  const methods = useForm<EmailFormSchemaType>({
    resolver: zodResolver(EmailFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });
  const {
    register,
    formState,
    handleSubmit: handleEmailSubmit,
    watch,
  } = methods;
  const { errors, isValid } = formState;
  let order: Order | null | undefined = workflow?.order;
  if (!order && params?.order) {
    try {
      order = JSON.parse(params.order);
    } catch {
      order = undefined;
    }
  }
  const tokens: (OrderToken | null)[] = order?.orderTokens ?? [];
  const emailToken = tokens.find(
    (k): k is OrderToken => !!k && k.key === 'EMAIL',
  );
  const recipientEmail = emailToken?.value || email;
  const {
    mutate: sendEmail,
    isPending,
    isSuccess,
  } = useMutation({
    mutationFn: async (data: EmailFormSchemaType) => {
      if (!data.email || !requestId || !order) return;
      return sendEmailMutationFn({ requestId, email: data.email });
    },
    onError: (error) => {
      errorToast({
        message:
          rawString({
            langKey: 'Global.confirmation.email.resend.submitError',
          }) ?? '',
      });
      clientLogger.logError(error);
    },
  });
  if (!order) return null;

  const handleClick = () => setIsInputOpen(!isInputOpen);

  const handleSubmit = handleEmailSubmit((data, e) => {
    e?.preventDefault();
    sendEmail(data);
  });

  const iconName = iconKey ? rawString({ langKey: iconKey }) : null;
  const isValidIconName = (
    name: string | null | undefined,
  ): name is IconNames =>
    name != null && iconNameOptions.includes(name as IconNames);

  const hasIcon = isValidIconName(iconName);

  const contentSection = (
    <Flex direction="column" gap="g-16">
      {descriptionKey && (
        <p>
          <Text
            langKey={descriptionKey}
            replacementTokens={{ email: recipientEmail ?? '' }}
          />
        </p>
      )}

      <Flex direction="column" gap="gy-8">
        {isSuccess ? (
          <Text
            langKey="Global.confirmation.email.resend.success"
            replacementTokens={{ email: watch('email') }}
          />
        ) : (
          <>
            <Button variant="plain" onClick={handleClick} className={s.button}>
              <Text langKey="Global.confirmation.email.resend.button" />
            </Button>
            {requestId && isInputOpen && (
              <InputRoot
                width="full"
                variant={errors.email ? 'error' : 'default'}
              >
                <InputGroup>
                  <InputField>
                    <InputLabel htmlFor="email">
                      <Text langKey="Global.confirmation.email.resend.placeholder" />
                    </InputLabel>
                    <InputControl
                      id="email"
                      {...register('email')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSubmit(e);
                        }
                      }}
                    />
                  </InputField>
                  <Button
                    isLoading={isPending}
                    ariaDisabled={!isValid}
                    onClick={handleSubmit}
                  >
                    <Text langKey="Global.submit" />
                  </Button>
                </InputGroup>
                {errors.email && (
                  <InputMessage>
                    <Icon name="warning" size="font" />
                    {errors.email.message}
                  </InputMessage>
                )}
              </InputRoot>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );

  return (
    <FormProvider {...methods}>
      <Flex className={s.container} direction="column" gap="g-16">
        {hasIcon ? (
          <Flex direction="row" gap="g-16">
            <Box className={s.icon}>
              <Icon size="lg" name={iconName} />
            </Box>
            {contentSection}
          </Flex>
        ) : (
          contentSection
        )}
      </Flex>
    </FormProvider>
  );
};
